import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const TripSidebar = ({ trip, onUpdateTrip }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [shareUrl, setShareUrl] = useState(`https://parktripplannerai.com/trip/${trip?.id}`);
  const [emailRecipient, setEmailRecipient] = useState('');

  const handleUpdatePackingItem = (itemIndex, checked) => {
    const updatedPackingList = [...trip?.packingList];
    updatedPackingList[itemIndex].checked = checked;
    onUpdateTrip({ ...trip, packingList: updatedPackingList });
  };

  const handleAddPackingItem = () => {
    const newItem = {
      id: Date.now(),
      item: "New item",
      checked: false,
      category: "General"
    };
    onUpdateTrip({ 
      ...trip, 
      packingList: [...trip?.packingList, newItem] 
    });
  };

  const handleShareTrip = () => {
    navigator.clipboard?.writeText(shareUrl);
    // Show toast notification
    alert('Trip link copied to clipboard!');
  };

  const handleEmailTrip = () => {
    if (emailRecipient) {
      // Simulate email sending
      alert(`Trip details sent to ${emailRecipient}`);
      setEmailRecipient('');
    }
  };

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-organic-sm transition-organic ${
        activeTab === id 
          ? 'bg-primary text-primary-foreground' 
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
      }`}
    >
      <Icon name={icon} size={16} />
      <span className="font-body font-body-medium text-sm">{label}</span>
    </button>
  );

  return (
    <div className="bg-card border border-border rounded-organic-lg p-6 sticky top-24">
      {/* Mobile Tabs */}
      <div className="flex space-x-1 mb-6 md:hidden">
        <TabButton id="details" label="Details" icon="Info" />
        <TabButton id="packing" label="Packing" icon="Package" />
        <TabButton id="share" label="Share" icon="Share2" />
      </div>
      {/* Desktop Navigation */}
      <div className="hidden md:flex flex-col space-y-1 mb-6">
        <TabButton id="details" label="Trip Details" icon="Info" />
        <TabButton id="packing" label="Packing List" icon="Package" />
        <TabButton id="share" label="Share Trip" icon="Share2" />
      </div>
      {/* Content */}
      <div className="space-y-6">
        {activeTab === 'details' && (
          <div className="space-y-4">
            <div>
              <h4 className="font-heading font-heading-semibold text-sm text-foreground mb-3">
                Trip Information
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-body text-sm text-muted-foreground">Duration</span>
                  <span className="font-body font-body-medium text-sm text-foreground">
                    {trip?.duration} days
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-body text-sm text-muted-foreground">Budget</span>
                  <span className="font-body font-body-medium text-sm text-foreground">
                    ${trip?.budget}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-body text-sm text-muted-foreground">Activities</span>
                  <span className="font-body font-body-medium text-sm text-foreground">
                    {trip?.totalActivities}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-heading font-heading-semibold text-sm text-foreground mb-3">
                Accommodation
              </h4>
              <div className="p-3 bg-muted/50 rounded-organic-sm">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Tent" size={16} className="text-primary" />
                  <span className="font-body font-body-medium text-sm text-foreground">
                    {trip?.accommodation?.type}
                  </span>
                </div>
                <p className="font-body text-sm text-muted-foreground">
                  {trip?.accommodation?.details}
                </p>
                {trip?.accommodation?.reservationLink && (
                  <a
                    href={trip?.accommodation?.reservationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 mt-2 text-primary hover:text-primary/80 transition-organic"
                  >
                    <Icon name="ExternalLink" size={14} />
                    <span className="font-body text-sm">View Reservation</span>
                  </a>
                )}
              </div>
            </div>

            <div>
              <h4 className="font-heading font-heading-semibold text-sm text-foreground mb-3">
                Weather Forecast
              </h4>
              <div className="space-y-2">
                {trip?.weather?.map((day, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-organic-sm">
                    <span className="font-body text-sm text-foreground">Day {index + 1}</span>
                    <div className="flex items-center space-x-2">
                      <Icon name={day?.icon} size={16} className="text-primary" />
                      <span className="font-body text-sm text-muted-foreground">
                        {day?.high}°/{day?.low}°
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'packing' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-heading font-heading-semibold text-sm text-foreground">
                Packing Checklist
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAddPackingItem}
                iconName="Plus"
                iconPosition="left"
              >
                Add Item
              </Button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {trip?.packingList?.map((item, index) => (
                <div key={item?.id} className="flex items-center space-x-3 p-2 hover:bg-muted/30 rounded-organic-sm">
                  <Checkbox
                    checked={item?.checked}
                    onChange={(e) => handleUpdatePackingItem(index, e?.target?.checked)}
                  />
                  <div className="flex-1">
                    <span className={`font-body text-sm ${
                      item?.checked ? 'line-through text-muted-foreground' : 'text-foreground'
                    }`}>
                      {item?.item}
                    </span>
                    <div className="text-xs text-muted-foreground">{item?.category}</div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pt-3 border-t border-border">
              <div className="text-sm text-muted-foreground">
                {trip?.packingList?.filter(item => item?.checked)?.length} of {trip?.packingList?.length} items packed
              </div>
            </div>
          </div>
        )}

        {activeTab === 'share' && (
          <div className="space-y-4">
            <div>
              <h4 className="font-heading font-heading-semibold text-sm text-foreground mb-3">
                Share Trip
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="font-body text-sm text-muted-foreground mb-2 block">
                    Trip Link
                  </label>
                  <div className="flex space-x-2">
                    <Input
                      value={shareUrl}
                      readOnly
                      className="flex-1 text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShareTrip}
                      iconName="Copy"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="font-body text-sm text-muted-foreground mb-2 block">
                    Send via Email
                  </label>
                  <div className="flex space-x-2">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={emailRecipient}
                      onChange={(e) => setEmailRecipient(e?.target?.value)}
                      className="flex-1 text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEmailTrip}
                      iconName="Send"
                      disabled={!emailRecipient}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-border">
              <h5 className="font-body font-body-medium text-sm text-foreground mb-2">
                Quick Actions
              </h5>
              <div className="space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  iconName="Download"
                  iconPosition="left"
                >
                  Download as PDF
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Add to Calendar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TripSidebar;